import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import type { ListParams, Resource } from '../api/resource';

export interface CrudOptions {
  /** Extra filters merged into every list request. */
  filters?: Record<string, string | number | boolean | undefined>;
  defaultSortBy?: string;
  pageSize?: number;
}

/**
 * List state (page, search, sort) plus the four mutations for one resource.
 *
 * Mutations invalidate the resource's whole query key rather than patching the
 * cache: a create can change which page a row lands on, and a soft delete can
 * change the total, so refetching is both simpler and correct.
 */
export function useCrud<TItem, TCreate, TUpdate>(
  key: string,
  api: Resource<TItem, TCreate, TUpdate>,
  options: CrudOptions = {},
) {
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(options.pageSize ?? 25);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState(options.defaultSortBy);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const params = useMemo<ListParams>(
    () => ({
      page,
      pageSize,
      search: search.trim() || undefined,
      sortBy,
      sortDir,
      ...options.filters,
    }),
    [page, pageSize, search, sortBy, sortDir, options.filters],
  );

  const list = useQuery({
    queryKey: [key, params],
    queryFn: () => api.list(params),
    // Keeps the previous page visible while the next one loads, instead of
    // collapsing the table to a spinner on every page change.
    placeholderData: (previous) => previous,
  });

  const invalidate = useCallback(
    () => queryClient.invalidateQueries({ queryKey: [key] }),
    [queryClient, key],
  );

  const create = useMutation({
    mutationFn: (body: TCreate) => api.create(body),
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: TUpdate }) => api.update(id, body),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.remove(id),
    onSuccess: invalidate,
  });

  /** Sorting by a new column starts ascending; the same column flips direction. */
  const toggleSort = useCallback(
    (field: string) => {
      setSortBy((current) => {
        setSortDir(current === field && sortDir === 'asc' ? 'desc' : 'asc');
        return field;
      });
    },
    [sortDir],
  );

  const applySearch = useCallback((value: string) => {
    setSearch(value);
    // A new search must not land the user on a page that no longer exists.
    setPage(1);
  }, []);

  return {
    list,
    create,
    update,
    remove,
    invalidate,
    page,
    setPage,
    pageSize,
    setPageSize,
    search,
    setSearch: applySearch,
    sortBy,
    sortDir,
    toggleSort,
  };
}
